/* eslint-disable react-refresh/only-export-components */
/**
 * Credits Context
 *
 * Global credits and storage management using React Context.
 * Real-time Firestore listener for live credit balance updates.
 *
 * CRITICAL CONSTRAINTS:
 * - NO localStorage/sessionStorage (React state + Firestore only)
 * - TypeScript strict mode (NO 'any' types)
 * - Real-time listener via onSnapshot (not one-time get)
 * - User-scoped data only
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebaseConfig';
import { useAuth } from '@/contexts/AuthContext';
import { UserDocument } from '@/shared/types';

export interface CreditsContextType {
  credits: number;
  storageUsed: number;
  storageLimit: number;
  loading: boolean;
  error: string | null;
}

// Create CreditsContext with undefined default
const CreditsContext = createContext<CreditsContextType | undefined>(undefined);

/**
 * Custom hook to access CreditsContext
 * @throws Error if used outside CreditsProvider
 */
export function useCredits(): CreditsContextType {
  const context = useContext(CreditsContext);
  if (!context) {
    throw new Error('useCredits must be used within CreditsProvider');
  }
  return context;
}

interface CreditsProviderProps {
  children: React.ReactNode;
}

/**
 * CreditsProvider Component
 *
 * Wraps app to provide global credits and storage tracking.
 * Uses real-time Firestore listener for live updates.
 */
export function CreditsProvider({ children }: CreditsProviderProps): React.JSX.Element {
  const { currentUser } = useAuth();
  const [credits, setCredits] = useState<number>(0);
  const [storageUsed, setStorageUsed] = useState<number>(0);
  const [storageLimit, setStorageLimit] = useState<number>(250 * 1024 * 1024); // 250MB default
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Real-time listener for user document
   * Updates credits and storage info automatically
   */
  useEffect(() => {
    if (!currentUser) {
      // Reset state when logged out
      setCredits(0);
      setStorageUsed(0);
      setStorageLimit(250 * 1024 * 1024);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    // Check for mock credits (E2E testing)
    // Check for mock credits (E2E testing)
    // @ts-expect-error - Mocking for E2E
    if (window.__MOCK_CREDITS__ !== undefined) {
      // @ts-expect-error - Mocking for E2E
      console.log('CreditsContext: Mocking credits', window.__MOCK_CREDITS__);
      // @ts-expect-error - Mocking for E2E
      setCredits(window.__MOCK_CREDITS__);
      setStorageUsed(0);
      setStorageLimit(250 * 1024 * 1024);
      setLoading(false);
      return;
    }

    // Set up real-time listener
    const unsubscribe = onSnapshot(
      doc(db, 'users', currentUser.uid),
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data() as UserDocument;
          setCredits(data.credits ?? 0);
          setStorageUsed(data.storageUsed ?? 0);
          setStorageLimit(data.storageLimit ?? 250 * 1024 * 1024);
          setError(null);
        } else {
          setError('User document not found');
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error listening to user document:', err);
        setError('Failed to load credits');
        setLoading(false);
      }
    );

    // Cleanup listener on unmount or user change
    return unsubscribe;
  }, [currentUser]);

  const value: CreditsContextType = {
    credits,
    storageUsed,
    storageLimit,
    loading,
    error,
  };

  return <CreditsContext.Provider value={value}>{children}</CreditsContext.Provider>;
}
